"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdapterSignatureSSS = void 0;
const security_1 = require("../../security");
const constants_1 = require("../constants");
// Types
const types_1 = require("../../security/authoriser/types");
const types_2 = require("../../security/types");
// Events
const events_1 = require("../../security/events");
class AdapterSignatureSSS {
    constructor(authoriser, eventBus) {
        this.subscribers = [];
        this.signatureId = null;
        this.challenge = types_1.ChallengeType.NONE;
        this.currentSubscriptions = [];
        this.initialize = async ({ resolve, response }) => {
            const challengeResponseData = response.data.data;
            this.resolve = resolve;
            this.originalRequest = response.calls.originCall;
            this.originalResponse = response;
            (0, events_1.publishShowSignatureEvent)({
                challenge: { data: undefined },
                state: types_2.SignatureStateEnum.AUTHENTICATION_REQUIRED,
                type: 'loading',
                success: false,
            });
            this.signatureService.reset();
            this.signatureId = challengeResponseData.progressId;
            this.signatureService.initializeAuthoriser(challengeResponseData, this.originalRequest, response.reference.url);
            this.signatureService.onStateChange((state) => this.onStateChangeCallback(state));
            (0, events_1.subscribeResentChallengeEvent)(this.onRetryChallengeStatusChange);
        };
        this.onStateChangeCallback = (state) => {
            this.currentSubscriptions.forEach(unsubscriber => unsubscriber());
            this.challenge = this.signatureService.getChallengeData()?.type ?? types_1.ChallengeType.NONE;
            if (state.isChallengeVerificationFailed) {
                this.handleValidationError();
                return;
            }
            if (state.isFailed) {
                this.handleError(state);
                return;
            }
            if (state.isChallengeLoading || state.isChallengeVerifying) {
                (0, events_1.publishShowSignatureEvent)({
                    challenge: { data: this.signatureService.getChallengeData() },
                    state: types_2.SignatureStateEnum.AUTHENTICATION_REQUIRED,
                    type: 'loading',
                    success: false,
                });
                return;
            }
            if (state.isFinishing) {
                return;
            }
            if (state.isReadyToFinish) {
                this.tryToFinish();
                return;
            }
            if (state.isStarted) {
                this.start();
            }
        };
        this.start = () => {
            this.stateMachine.init({ state: types_2.SignatureStateEnum.AUTHENTICATION_REQUIRED, type: this.challenge });
            this.currentSubscriptions.push((0, events_1.subscribeValidateSignatureEvent)((credentials) => {
                if (!credentials) {
                    return this.retryChallenge();
                }
                this.validateCredentials(credentials);
            }));
            this.currentSubscriptions.push((0, events_1.subscribeCancelSignatureEvent)(this.cancel), (0, events_1.subscribeResetSignatureEvent)(this.reset));
            (0, events_1.publishShowSignatureEvent)({
                challenge: { data: this.signatureService.getChallengeData() },
                state: types_2.SignatureStateEnum.AUTHENTICATION_REQUIRED,
                success: true,
            });
        };
        this.onRetryChallengeStatusChange = ({ success }) => {
            (0, events_1.publishShowSignatureEvent)({
                challenge: {
                    data: this.signatureService.getChallengeData(),
                    retryStatus: success ? types_2.RetryStatuses.SUCCESS : types_2.RetryStatuses.ERROR,
                },
                state: types_2.SignatureStateEnum.AUTHENTICATION_REQUIRED,
                success: true,
            });
        };
        this.retryChallenge = async () => {
            try {
                (0, events_1.publishShowSignatureEvent)({
                    challenge: {
                        data: this.signatureService.getChallengeData(),
                        retryStatus: types_2.RetryStatuses.LOADING,
                    },
                    state: types_2.SignatureStateEnum.AUTHENTICATION_REQUIRED,
                    success: true,
                });
                return await this.signatureService.retryChallenge();
            }
            catch (error) {
                return this.setError(types_2.SignatureStateEnum.TECHNICAL_ERROR, types_2.SignatureError.GENERIC);
            }
        };
        this.validateCredentials = async (credentials) => {
            if (!credentials) {
                throw Error('Credentials cannot be null');
            }
            try {
                await this.signatureService.verifyChallenge(credentials);
            }
            catch (err) {
                const error = err;
                if (error.httpStatus === constants_1.STATUS_ERROR) {
                    this.setError(types_2.SignatureStateEnum.TECHNICAL_ERROR, types_2.SignatureError.GENERIC);
                }
                else {
                    this.setError(types_2.SignatureStateEnum.SIGNATURE_FAILED, types_2.SignatureError.GENERIC);
                }
            }
        };
        this.handleValidationError = () => {
            this.stateMachine.init({ state: types_2.SignatureStateEnum.TECHNICAL_ERROR });
            (0, events_1.publishShowSignatureEvent)({
                challenge: { data: this.signatureService.getChallengeData() },
                state: types_2.SignatureStateEnum.TECHNICAL_ERROR,
                type: types_2.SignatureError.FAILED_VERIFICATION,
                success: false,
            });
        };
        this.getCurrentState = () => this.stateMachine.getCurrentState();
        this.setError = (state, type = types_2.SignatureError.GENERIC) => {
            this.signatureService.fail(state === types_2.SignatureStateEnum.SIGNATURE_FAILED, type);
            this.stateMachine.onTransition({ state });
            (0, events_1.publishErrorSignatureEvent)({ state, type });
        };
        this.handleError = (state) => {
            this.stateMachine.init({ state: types_2.SignatureStateEnum.SIGNATURE_FAILED });
            let type;
            if (state.error === types_2.SignatureError.EXPIRED_SIGNATURE_KEY) {
                type = types_2.SignatureError.EXPIRED_SIGNATURE_KEY;
            }
            else if (state.isBlocked || state.isChallengeBlocked) {
                type = types_2.SignatureError.CONSECUTIVE_ERRORS;
            }
            else {
                type = types_2.SignatureError.GENERIC;
            }
            (0, events_1.publishErrorSignatureEvent)({
                state: types_2.SignatureStateEnum.SIGNATURE_FAILED,
                type,
            });
        };
        this.cancel = () => {
            if (this.stateMachine.getCurrentState().state === types_2.SignatureStateEnum.SIGNATURE_COMPLETED ||
                this.stateMachine.getCurrentState().state === types_2.SignatureStateEnum.IDLE) {
                return;
            }
            this.signatureService.reset();
            this.stateMachine.onTransition({ state: types_2.SignatureStateEnum.SIGNATURE_CANCELLED });
            (0, events_1.publishShowSignatureEvent)({
                state: types_2.SignatureStateEnum.SIGNATURE_CANCELLED,
                success: true,
                challenge: { data: types_1.ChallengeType.NONE },
            });
            this.resolve?.(this.originalResponse);
            this.clearRequestInfo();
        };
        this.tryToFinish = () => {
            this.isSignatureConfirmationProcessing(true);
            this.originalRequest?.();
        };
        this.finishSuccessfully = (response) => {
            this.resolve?.(response);
            this.stateMachine.onTransition({ state: types_2.SignatureStateEnum.SIGNATURE_COMPLETED });
            (0, events_1.publishFinishSignatureEvent)({ state: types_2.SignatureStateEnum.SIGNATURE_COMPLETED });
            this.clearRequestInfo();
        };
        this.clearRequestInfo = () => {
            this.signatureId = null;
            this.originalRequest = undefined;
            this.resolve = undefined;
        };
        this.reset = () => {
            this.signatureService.reset();
            this.stateMachine.setState({ state: types_2.SignatureStateEnum.IDLE });
            (0, events_1.publishShowSignatureEvent)({
                challenge: { data: undefined },
                state: types_2.SignatureStateEnum.IDLE,
                success: false,
            });
            this.clearRequestInfo();
        };
        this.getSignatureId = () => this.signatureId ?? '';
        this.isSignatureProcessStarted = () => this.stateMachine.getCurrentState().state === types_2.SignatureStateEnum.AUTHENTICATION_REQUIRED && this.signatureService.state.isStarted;
        if (!eventBus) {
            throw new Error('EventBus is required');
        }
        this.signatureService = authoriser;
        this.stateMachine = new security_1.StateMachine({ stateList: types_2.signatureStates, inititialState: { state: types_2.SignatureStateEnum.IDLE } });
        this.eventBus = eventBus;
    }
    onStateChange(callback) {
        this.subscribers.push(callback);
        this.stateMachine.setSubscribers(this.subscribers);
    }
    isSignatureLastProcess(requestId) {
        return !!this.signatureService.progressId && requestId === this.signatureService.requestId;
    }
    isSignatureConfirmationProcessing(isConfirming) {
        this.signatureService.updateState({ isFinishing: isConfirming });
    }
}
exports.AdapterSignatureSSS = AdapterSignatureSSS;
//# sourceMappingURL=AdapterSignatureSSS.js.map